/* ZoneComponent.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ZoneRenderer");
const _ = require("lodash");
const Uuid = require("uuid");

/**
 * class ZoneComponent
 *
 * When working on ZoneComponent it is important to understand the distinction between EngineZoneEntities
 * and OrgZoneEntities.
 *
 * EngineZoneEntities: These are the zones that jobs will ultimately run against. The engine zones are
 * created typically at the root org.  Once created, engines will be assigned to the zone so that the zone
 * becomes active and can have jobs run against it.  The state of the engine zone is determined by whether
 * engines are running in the zone.  When a device is assigned an engine zone, that device will now send
 * jobs to queues that a group of engines are listening on.  The engines will process the job request and
 * return the results.  If the device specified zone is offline and the device is not locked to the zone,
 * then the system will attempt to route the job to a default zone specified in the org hierarchy.
 *
 * OrgZoneEntitites: These entities are created and destroyed by clicking the "Manage Zones..." option in the
 * zone panel.  OrgZones are created against the current org and will associated with the engine zones in
 * the org hierarchy with a one to one relationship between OrgZone and EngineZone.  The OrgZones at the org
 * level allow the zones to be enabled or disabled at the current
 * org level, thus allowing the user to define which zones that want enabled for the current org.  This is
 * a level of indirection that is needed so that engine zones do not have to be created at each org level
 * since the engine zones are a very finite resource in the system.
 *
 */
class ZoneComponent extends EventEmitter {
    /**
     *
     * @param {EnginesEntity.} EnginesEntity
     * @param {UserSession} userSession
     * @param {UiUtils} uiUtils
     * @param {CoreLogger} logger
     */
    constructor(
        orgZoneEntity,
        engineZoneEntity,
        licenseStateEntity,
        userSession,
        uiUtils,
        userFeedback,
        logger
    ) {
        super();

        this._orgZoneEntity = orgZoneEntity;
        this._engineZoneEntity = engineZoneEntity;
        this._licenseStateEntity = licenseStateEntity;
        this._userSession = userSession;
        this._userFeedback = userFeedback;
        this._authorizedActions = userSession.authorizedActions;
        this._logger = logger;

        /**
         *
         * @type {EnginesEntity|undefined}
         * @private
         */
        this._orgZoneEntityFeed = undefined;
        this._engineZoneFeed = undefined;

        /**
         * @typedef {Object<string, Object>} ModelData - origin data stored in the model
         *
         * @type {ModelData}
         * @private
         */
        this._data = {};

        /**
         *
         * @type {?{inherited: string[], editable: string[], systemOwned: string[]}};
         * @private
         */
        this._fieldsKeys = {inherited: [], editable: [], systemOwned: []};

        this._renderer = new Renderer(this, uiUtils);
        this._managingZones = false;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _triggerDataUpdated() {
        /**
         * @event ZoneComponent#DataUpdated
         */
        this.emit("DataUpdated");
    }

    _triggerFeedReset() {
        this.emit("FeedReset");
    }

    _triggerOrgChange() {
        this.emit("OrgChanged");
    }

    _registerListeners() {}

    _unregisterListeners() {
        this._orgZoneEntityFeed.removeListener(
            "EntityPropertiesUpdated",
            this._onOrgZoneFeedUpdate
        );
        this._engineZoneFeed.removeListener("EntityPropertiesUpdated", this._onZoneFeedUpdate);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        return this._shutdownFeeds()
            .then(() => {
                return this._renderer.shutdown();
            })
            .then(
                () => {
                    this._orgZoneEntity = null;
                    this._engineZoneEntity = null;
                    this._userSession = null;
                    this._logger = null;
                    this._orgZoneEntityFeed = null;
                    this._engineZoneFeed = null;
                    this._data = null;
                    this._fieldsKeys = null;
                    this._renderer = null;
                },
                (err) => {
                    console.log("Error shutting down EnginesComponent: ", err);
                }
            );
    }

    /**
     * This function deletes the Engine Zone record using the OrgZoneId that points to it.
     * @param engineZoneId
     * @returns {Promise<void>}
     */
    async deleteEngineZone(engineZoneId) {
        let engineZoneEntity = this._engineZoneFeed.getInstanceById(engineZoneId);
        await engineZoneEntity.delete();
        setTimeout(() => {
            this._triggerDataUpdated();
        }, 100);
    }

    async updateOrgSpecificZoneData(zones) {
        let zoneKeys = Object.keys(zones);
        for (let x = 0; x < zoneKeys.length; x++) {
            let zone = zones[zoneKeys[x]];
            let orgZoneEntity = this._orgZoneEntityFeed.getInstanceById(zoneKeys[x]);
            if (orgZoneEntity) {
                this._setEntityFromInstance(orgZoneEntity, zone);
                if (orgZoneEntity.isDirty()) {
                    await orgZoneEntity.update();
                }
            } else {
                orgZoneEntity = new this._orgZoneEntity(this.activeOrgId);
                this._setEntityFromInstance(orgZoneEntity, zone);
                await orgZoneEntity.createStatic();
            }
            let engineZoneEntity = this._engineZoneFeed.getInstanceById(zone.engineZoneId);
            engineZoneEntity.setDisplayName(zone.name);
            if (engineZoneEntity.isDirty()) {
                await engineZoneEntity.update();
            }
        }
    }

    /**
     * Create the org zone entities at this org level to start managing
     * zones at this org.
     * @returns {Promise<void>}
     */
    async createOrgZonesToManage() {
        let parentOrgZones = this.getOrgZones();
        // organize by engineId
        let parentOrgZoneEngines = {};
        for (let key in parentOrgZones) {
            parentOrgZoneEngines[parentOrgZones[key].engineZoneId] = parentOrgZones[key];
        }
        let engineKeys = Object.keys(this.getEngineZones());
        let singleDefaultZoneSet = false;
        for (let engineKey of engineKeys) {
            let engineZoneEntity = this.engineZones[engineKey];
            let orgZoneEntity = new this._orgZoneEntity(this.activeOrgId);
            let enabled = true;
            let defaultZone = false;
            let allowForExtraCapacityUsage = false;
            // Inherit from the parents
            if (parentOrgZoneEngines[engineKey]) {
                enabled = parentOrgZoneEngines[engineKey].getZoneEnabled();
                defaultZone = parentOrgZoneEngines[engineKey].getDefaultZone();
                allowForExtraCapacityUsage =
                    !!parentOrgZoneEngines[engineKey].getAllowForExtraCapacityUsage();
            } else {
                defaultZone = engineZoneEntity.getDefaultZone();
            }
            // Only one zone should be marked as default so only mark the first one
            // found.
            if (defaultZone) {
                if (singleDefaultZoneSet) {
                    defaultZone = false;
                } else {
                    singleDefaultZoneSet = true;
                }
            }
            orgZoneEntity.setZoneEnabled(enabled);
            orgZoneEntity.setDefaultZone(defaultZone);
            orgZoneEntity.setAllowForExtraCapacityUsage(allowForExtraCapacityUsage);
            orgZoneEntity.setEngineZoneRelationship(engineKey);
            orgZoneEntity.setName(engineZoneEntity.displayName);
            await orgZoneEntity.createStatic();
        }
        await this._resetFeeds();
    }

    async clearOrgZonesForOrg() {
        let orgZones = this.getOrgZones();
        let orgZoneKeys = Object.keys(orgZones);
        for (let orgZoneKey of orgZoneKeys) {
            let orgZoneEntity = orgZones[orgZoneKey];
            if (orgZoneEntity.tenantId === this.activeOrgId) {
                await orgZoneEntity.delete();
            }
        }
        await this._resetFeeds();
    }

    /****************************************/
    /* GETTERS AND SETTERS                  */
    /****************************************/

    isInherited(engineZoneId) {
        return (
            _.get(this._engineZoneFeed.getCache()[engineZoneId], "tenantId") !== this.activeOrgId
        );
    }

    isAuthorizedToManageZones() {
        let authorizedActions = this._userSession.authorizedActions;
        return authorizedActions.indexOf("orgZoneAccess") !== -1;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    getManagingZones() {
        return this._managingZones;
    }

    setManagingZones(value) {
        this._managingZones = value;
    }

    getZonesData() {
        return this._startOrgZoneFeed(this.activeOrgId).then(() => {
            return this._startEngineZoneFeed(this.activeOrgId);
        });
    }

    get orgZones() {
        return this.getOrgZones();
    }

    getOrgZones() {
        return this._orgZoneEntityFeed.cache;
    }

    get engineZones() {
        return this.getEngineZones();
    }

    getEngineZones() {
        return this._engineZoneFeed.cache;
    }

    get engineZoneEntity() {
        return this.getEngineZoneEntity();
    }

    getEngineZoneEntity() {
        return this._engineZoneEntity;
    }

    get engineZoneEntityFeed() {
        return this.getEngineZoneEntityFeed();
    }

    getEngineZoneEntityFeed() {
        return this._engineZoneFeed;
    }

    get userFeedback() {
        return this.getUserFeedback();
    }

    getUserFeedback() {
        return this._userFeedback;
    }

    // Determine if we are managing org zones at this org level.
    isManagingOrgZones() {
        let orgLevelManagement = this._managingZones;
        for (let orgZoneKey in this.orgZones) {
            if (this.orgZones[orgZoneKey].tenantId === this.getActiveOrgId()) {
                orgLevelManagement = true;
            }
        }
        return orgLevelManagement;
    }

    /**
     * Determine if there is a license at this org level.
     * @returns {Promise<boolean>}
     */
    async orgHasLicense() {
        let licenses = await this._licenseStateEntity.loadLicenseStates(this.getActiveOrgId());
        return !_.isEmpty(licenses);
    }

    /**
     * Check if a zone exist at the current org level.
     */
    zoneExistsAtOrgLevel() {
        let zoneAtOrgLevel = false;
        for (let engineZoneKey in this.getEngineZones()) {
            let engineZone = this.getEngineZones()[engineZoneKey];
            if (engineZone.tenantId === this.getActiveOrgId()) {
                zoneAtOrgLevel = true;
            }
        }
        return zoneAtOrgLevel;
    }

    /*******************************************/
    /* START HANDLER METHODS                   */
    /*******************************************/

    whenModelIsReady() {
        return this.getZonesData();
    }

    async organizationChanged(/*orgObj*/) {
        this._managingZones = false;
        await this._resetFeeds();
        this._triggerOrgChange();
    }

    _onOrgZoneFeedUpdate() {
        this._triggerDataUpdated();
    }

    _onZoneFeedUpdate(/*engineZoneEntity, patches*/) {
        this._triggerDataUpdated();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    async _resetFeeds() {
        await this._shutdownFeeds();
        await this.getZonesData();
        this._triggerDataUpdated();
        this._triggerFeedReset();
    }

    _setOrgZoneFeed(orgZoneFeed) {
        this._orgZoneEntityFeed = orgZoneFeed;
        this._orgZoneEntityFeed.on(
            "EntityPropertiesUpdated",
            (this._onOrgZoneFeedUpdate = this._onOrgZoneFeedUpdate.bind(this))
        );
        return this._orgZoneEntityFeed;
    }

    _setEngineZoneFeed(zoneFeed) {
        this._engineZoneFeed = zoneFeed;
        this._engineZoneFeed.on(
            "EntityPropertiesUpdated",
            (this._onZoneFeedUpdate = this._onZoneFeedUpdate.bind(this))
        );
        return this._engineZoneFeed;
    }

    _startOrgZoneFeed(tenantId) {
        if (!this._orgZoneEntityFeed) {
            return this._orgZoneEntity
                .startAscendOrgZonesFeed(tenantId)
                .whenInitialized()
                .then((orgZoneEntity) => this._setOrgZoneFeed(orgZoneEntity));
        } else {
            return Promise.resolve().then(() => {
                return this._orgZoneEntityFeed.reestablishFeed(tenantId);
            });
        }
    }

    _startEngineZoneFeed(tenantId) {
        if (!this._engineZoneFeed) {
            return this._engineZoneEntity
                .startAscendEngineZonesFeed(tenantId)
                .whenInitialized()
                .then((zoneFeed) => this._setEngineZoneFeed(zoneFeed));
        } else {
            return Promise.resolve().then(() => {
                return this._engineZoneFeed.reestablishFeed(tenantId);
            });
        }
    }

    async _shutdownFeeds() {
        try {
            if (this._orgZoneEntityFeed) {
                await this._orgZoneEntityFeed.shutdown();
            }
            if (this._engineZoneFeed) {
                await this._engineZoneFeed.shutdown();
            }
            this._unregisterListeners();
            this._orgZoneEntityFeed = undefined;
            this._engineZoneFeed = undefined;
        } catch (error) {
            this._logger.warn(error);
        }
    }

    _setEntityFromInstance(orgZoneEntity, instance) {
        orgZoneEntity.setZoneEnabled(instance.enabled);
        // orgZoneEntity.setEngineZoneRelationship(instance.engineZoneId);
        orgZoneEntity.setDefaultZone(instance.defaultZone);
        orgZoneEntity.setName(instance.name);
        orgZoneEntity.setAllowForExtraCapacityUsage(instance.allowForExtraCapacityUsage);
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /**
     *
     * @returns {{dataType: string, name: string, id: string, enabled: boolean}}
     *
     */
    createCustomFieldItem() {
        return {dataType: "String", name: "New Field", id: Uuid.v4(), enabled: true, isNew: true};
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START DATA VALIDATION MEtHODS           */
    /*******************************************/

    /*******************************************/
    /* START DATA VALIDATION MEtHODS           */
    /*******************************************/
}

module.exports = ZoneComponent;

ZoneComponent.$groupName = "Organization";

ZoneComponent.$displayName = "Zones";
ZoneComponent.$license = ["GLUWARE"];

ZoneComponent.$inject = [
    "$teve.entities.OrgZone",
    "$teve.entities.EngineZone",
    "$teve.entities.LicenseState",
    "$teve.userSession",
    "$teve.ui.UiUtils",
    "$teve.ui.userFeedback",
    "$teve.logger",
];
